'use client';

import { useMemo, useEffect, useReducer, useCallback } from 'react';
import { signIn, signOut } from '@the-hyoosik/nexus-api-connector-js/authentication';
import {
  KioskAccommodation,
  KioskAccommodationStatus,
} from '@the-hyoosik/nexus-api-connector-js/dist/domain/kiosk-accommodation';
import {
  Accommodation,
  AccommodationStatus,
  AccommodationSortType,
} from '@the-hyoosik/nexus-api-connector-js/dist/domain/accommodation';
import {
  MemberDetail,
  fetchCurrentMember,
  fetchAccommodationListOfMember,
  fetchKioskAccommodationListOfMember,
} from '@the-hyoosik/nexus-api-connector-js/member';

import { AuthContext } from './auth-context';
import { ActionMapType, AuthStateType } from '../../types';
import { setLocal, isValidToken, getRefreshToken, isGeneralMember } from './utils';

// ----------------------------------------------------------------------
/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */
// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  CHANGE_ACCOMMODATION = 'CHANGE_ACCOMMODATION',
}

type Payload = {
  [Types.INITIAL]: {
    member: MemberDetail | null;
    accommodationList: Accommodation[] | null;
    currentAccommodation: Accommodation | null;
    currentKioskAccommodation: KioskAccommodation | null;
    kioskAccommodationList: KioskAccommodation[] | null;
  };
  [Types.LOGIN]: {
    member: MemberDetail | null;
    accommodationList: Accommodation[] | null;
    currentAccommodation: Accommodation | null;
    currentKioskAccommodation: KioskAccommodation | null;
    kioskAccommodationList: KioskAccommodation[] | null;
    accessToken: string;
  };
  [Types.REGISTER]: {
    member: MemberDetail | null;
    accessToken: string;
  };
  [Types.LOGOUT]: undefined;
  [Types.CHANGE_ACCOMMODATION]: {
    currentAccommodation: Accommodation | null;
    currentKioskAccommodation: KioskAccommodation | null;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  member: null,
  accommodationList: [],
  currentAccommodation: null,
  currentKioskAccommodation: null,
  kioskAccommodationList: [],
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      member: action.payload.member,
      accommodationList: action.payload.accommodationList,
      currentAccommodation: action.payload.currentAccommodation,
      currentKioskAccommodation: action.payload.currentKioskAccommodation,
      kioskAccommodationList: action.payload.kioskAccommodationList,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      member: action.payload.member,
      accommodationList: action.payload.accommodationList,
      currentAccommodation: action.payload.currentAccommodation,
      currentKioskAccommodation: action.payload.currentKioskAccommodation,
      kioskAccommodationList: action.payload.kioskAccommodationList,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      member: action.payload.member,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      member: null,
      accommodationList: null,
      currentAccommodation: null,
      currentKioskAccommodation: null,
      kioskAccommodationList: null,
    };
  }
  if (action.type === Types.CHANGE_ACCOMMODATION) {
    return {
      ...state,
      currentAccommodation: action.payload.currentAccommodation,
      currentKioskAccommodation: action.payload.currentKioskAccommodation,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const STORAGE_KEY = 'accessToken';
export const STORAGE_ACCOMMODATION_ID = 'accommodationId';
export const STORAGE_KIOSK_ACCOMMODATION_ID = 'kioskAccommodationId';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setLocal(accessToken);
        await getRefreshToken();

        const member = await fetchCurrentMember({ accessToken });

        let { accommodationList } = await fetchAccommodationListOfMember({
          accessToken,
          memberId: member.currentMember.id,
          status: [AccommodationStatus.ON_SALE],
          sort: AccommodationSortType.NAME_ASC,
        });

        if (isGeneralMember(member.currentMember) && accommodationList.length > 0) {
          accommodationList = [accommodationList[0]];
        }

        let currentAccommodation: Accommodation | null = null;
        if (accommodationList && accommodationList.length > 0) {
          const storedAccommodationId = localStorage.getItem(STORAGE_ACCOMMODATION_ID);
          if (storedAccommodationId) {
            currentAccommodation =
              accommodationList.find((accommodation) => accommodation.id === Number(storedAccommodationId)) || null;
          }
          if (!currentAccommodation) {
            currentAccommodation = accommodationList[0];
          }
          // 현재 숙소 ID를 localStorage에 저장 (Smart Pricing 등에서 참조)
          localStorage.setItem(STORAGE_ACCOMMODATION_ID, String(currentAccommodation.id));
        }

        const kioskAccommodationList: KioskAccommodation[] = [];
        if (member.currentMember.isKioskMember) {
          const { kioskAccommodationList: _kioskAccommodationList } = await fetchKioskAccommodationListOfMember({
            accessToken,
            withoutPage: true,
            memberId: member.currentMember.id,
            statuses: [KioskAccommodationStatus.ON_SALE],
          });

          kioskAccommodationList.push(..._kioskAccommodationList);
        }

        let currentKioskAccommodation: KioskAccommodation | null = null;
        if (currentAccommodation && kioskAccommodationList.length > 0) {
          currentKioskAccommodation =
            kioskAccommodationList.find((ka) => ka.nexusAccommodationId === currentAccommodation?.id) || null;
        }

        dispatch({
          type: Types.INITIAL,
          payload: {
            member: {
              ...member.currentMember,
            },
            accommodationList,
            currentAccommodation: currentAccommodation
              ? {
                ...currentAccommodation,
              }
              : null,
            currentKioskAccommodation: currentKioskAccommodation ? { ...currentKioskAccommodation } : null,
            kioskAccommodationList,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            member: null,
            accommodationList: null,
            currentAccommodation: null,
            currentKioskAccommodation: null,
            kioskAccommodationList: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          member: null,
          accommodationList: null,
          currentAccommodation: null,
          currentKioskAccommodation: null,
          kioskAccommodationList: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const data = {
      email,
      password,
    };

    const { accessToken } = await signIn({
      username: data.email,
      password: data.password,
    });

    const { currentMember } = await fetchCurrentMember({ accessToken });

    if (!currentMember.isMember) {
      throw new Error('Not a member');
    }

    setLocal(accessToken);

    let { accommodationList } = await fetchAccommodationListOfMember({
      accessToken,
      memberId: currentMember.id,
      status: [AccommodationStatus.ON_SALE],
    });

    if (isGeneralMember(currentMember) && accommodationList.length > 0) {
      accommodationList = [accommodationList[0]];
    }

    let currentAccommodation: Accommodation | null = null;
    if (accommodationList && accommodationList.length > 0) {
      const storedAccommodationId = localStorage.getItem(STORAGE_ACCOMMODATION_ID);
      if (storedAccommodationId) {
        currentAccommodation =
          accommodationList.find((accommodation) => accommodation.id === Number(storedAccommodationId)) || null;
      }
      if (!currentAccommodation) {
        currentAccommodation = accommodationList[0];
      }
    }

    const kioskAccommodationList: KioskAccommodation[] = [];
    if (currentMember.isKioskMember) {
      const { kioskAccommodationList: _kioskAccommodationList } = await fetchKioskAccommodationListOfMember({
        accessToken,
        withoutPage: true,
        memberId: currentMember.id,
        statuses: [KioskAccommodationStatus.ON_SALE],
      });

      kioskAccommodationList.push(..._kioskAccommodationList);
    }

    let currentKioskAccommodation: KioskAccommodation | null = null;
    if (currentAccommodation && kioskAccommodationList.length > 0) {
      currentKioskAccommodation =
        kioskAccommodationList.find((ka) => ka.nexusAccommodationId === currentAccommodation?.id) || null;
    }

    dispatch({
      type: Types.LOGIN,
      payload: {
        member: {
          ...currentMember,
        },
        accommodationList,
        currentAccommodation: currentAccommodation
          ? {
            ...currentAccommodation,
          }
          : null,
        currentKioskAccommodation: currentKioskAccommodation ? { ...currentKioskAccommodation } : null,
        kioskAccommodationList,
        accessToken,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email: string, password: string, firstName: string, lastName: string) => {
    // const data = {
    //   email,
    //   password,
    //   firstName,
    //   lastName,
    // };
    // const res = await axios.post(endpoints.auth.register, data);
    // const { accessToken, member } = res.data;
    // localStorage.setItem(STORAGE_KEY, accessToken);
    // dispatch({
    //   setting-setting: Types.REGISTER,
    //   payload: {
    //     member: {
    //       ...member,
    //       accessToken,
    //     },
    //   },
    // });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    const accessToken = localStorage.getItem(STORAGE_KEY);

    if (accessToken) {
      await signOut({ accessToken });
      localStorage.removeItem(STORAGE_KEY);
    }

    setLocal(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const changeAccommodation = useCallback(
    async (accommodationId: number) => {
      const accommodation = state.accommodationList?.find((a) => a.id === accommodationId);
      if (accommodation) {
        localStorage.setItem(STORAGE_ACCOMMODATION_ID, String(accommodationId));
        const currentKioskAccommodation =
          state.kioskAccommodationList?.find((ka) => ka.nexusAccommodationId === accommodation.id) || null;

        dispatch({
          type: Types.CHANGE_ACCOMMODATION,
          payload: {
            currentAccommodation: accommodation,
            currentKioskAccommodation,
          },
        });
      }
    },
    [state.accommodationList, state.kioskAccommodationList]
  );

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.member ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      member: state.member,
      accommodationList: state.accommodationList ? state.accommodationList : null,
      currentAccommodation: state.currentAccommodation ? state.currentAccommodation : null,
      currentKioskAccommodation: state.currentKioskAccommodation ? state.currentKioskAccommodation : null,
      kioskAccommodationList: state.kioskAccommodationList ? state.kioskAccommodationList : null,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      changeAccommodation,
    }),
    [
      login,
      logout,
      register,
      state.accommodationList,
      state.currentAccommodation,
      state.currentKioskAccommodation,
      state.kioskAccommodationList,
      state.member,
      status,
      changeAccommodation,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
